import { useEffect, useMemo, useState } from 'react';
import CmsWorkflowPanel from './CmsWorkflowPanel';
import AdmissionsAdminPanel from './AdmissionsAdminPanel';
import SuperAdminDashboard from './SuperAdminDashboard';

function roleCodeToLabel(roleCode) {
  if (roleCode === 'admin') {
    return 'Administrator';
  }
  if (roleCode === 'super_admin') {
    return 'Super Admin';
  }
  if (roleCode === 'editor') {
    return 'Editor';
  }
  return roleCode;
}

async function apiRequest(apiBase, path, options = {}) {
  const response = await fetch(`${apiBase}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
    ...options,
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.ok) {
    throw new Error(payload.message || 'Request failed');
  }

  return payload;
}

function SuperAdminPanel({ apiBase, currentUser, onLogin, onLogout }) {
  const [email, setEmail] = useState('superadmin@permataharapansecondary.sch.id');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [updateHistory, setUpdateHistory] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [activeView, setActiveView] = useState('users');
  const [showCreateUserForm, setShowCreateUserForm] = useState(false);

  const [newUserForm, setNewUserForm] = useState({
    full_name: '',
    email: '',
    password: '',
    role_codes: ['editor'],
    is_active: true,
  });

  const [userEdits, setUserEdits] = useState({});

  const activeUsersCount = useMemo(
    () => users.filter((user) => Boolean(user.is_active)).length,
    [users]
  );

  const inReviewCount = useMemo(
    () => updateHistory.filter((entry) => ['pending', 'approved', 'draft'].includes(entry.status_code)).length,
    [updateHistory]
  );

  const adminRoleOptions = useMemo(
    () => roles.filter((role) => ['admin', 'super_admin', 'editor', 'approver', 'admissions', 'recruiter'].includes(role.role_code)),
    [roles]
  );

  useEffect(() => {
    if (!currentUser || true) {
      return;
    }

    async function loadAdminData() {
      setLoadingData(true);
      setError('');
      try {
        const [rolesRes, usersRes, updatesRes] = await Promise.all([
          apiRequest(apiBase, '/admin/roles'),
          apiRequest(apiBase, '/admin/users'),
          apiRequest(apiBase, '/admin/update-history'),
        ]);

        setRoles(rolesRes.data || []);
        setUsers(usersRes.data || []);
        setUpdateHistory(updatesRes.data || []);

        const editMap = {};
        (usersRes.data || []).forEach((user) => {
          editMap[user.id] = {
            role_codes: user.role_codes || [],
            is_active: Boolean(user.is_active),
          };
        });
        setUserEdits(editMap);
      } catch (err) {
        setError(err.message || 'Failed to load admin data');
      } finally {
        setLoadingData(false);
      }
    }

    loadAdminData();
  }, [apiBase, currentUser]);

  async function handleLoginSubmit(event) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const payload = await apiRequest(apiBase, '/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      onLogin(payload.data.user);
      setPassword('');
      setSuccess('Login successful. Super Admin panel is now active.');
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleLogout() {
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      await apiRequest(apiBase, '/auth/logout', { method: 'POST' });
      onLogout();
      setSuccess('Logged out from Super Admin panel.');
    } catch (err) {
      setError(err.message || 'Logout failed');
    } finally {
      setSubmitting(false);
    }
  }

  function onRoleToggle(formState, roleCode) {
    const exists = formState.role_codes.includes(roleCode);
    if (exists) {
      return formState.role_codes.filter((code) => code !== roleCode);
    }
    return [...formState.role_codes, roleCode];
  }

  async function handleCreateUser(event) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      await apiRequest(apiBase, '/admin/users', {
        method: 'POST',
        body: JSON.stringify(newUserForm),
      });
      setSuccess('User created and access roles assigned successfully.');
      setNewUserForm({
        full_name: '',
        email: '',
        password: '',
        role_codes: ['editor'],
        is_active: true,
      });
      setShowCreateUserForm(false);

      const usersRes = await apiRequest(apiBase, '/admin/users');
      setUsers(usersRes.data || []);
    } catch (err) {
      setError(err.message || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleUpdateUserAccess(userId) {
    const patch = userEdits[userId];
    if (!patch) {
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      await apiRequest(apiBase, `/admin/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(patch),
      });
      setSuccess('User access updated.');

      const usersRes = await apiRequest(apiBase, '/admin/users');
      setUsers(usersRes.data || []);
    } catch (err) {
      setError(err.message || 'Failed to update user access');
    } finally {
      setSubmitting(false);
    }
  }

  if (!currentUser) {
    return (
      <section className="super-admin-section">
        <div className="super-admin-shell">
          <h2>Super Admin Login</h2>
          <p className="super-admin-subtitle">
            Sign in to manage users and monitor content update status.
          </p>

          <p className="super-admin-login-note">
            This portal is only accessible through the administrator URL.
          </p>

          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-info">{success}</div>}

          <form className="super-admin-login-form" onSubmit={handleLoginSubmit}>
            <label>
              Email
              <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
            </label>
            <label>
              Password
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </label>
            <button type="submit" disabled={submitting}>
              {submitting ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>
      </section>
    );
  }

  if (currentUser) {
    return (
      <SuperAdminDashboard
        user={currentUser}
        onLogout={handleLogout}
        apiBase={apiBase}
      />
    );
  }

  return (
    <section className="super-admin-section">
      <div className="super-admin-shell super-admin-shell-dashboard">
        <div className="super-admin-headline">
          <div>
            <p className="super-admin-kicker">Administrator</p>
            <h2>Super Admin Dashboard</h2>
            <p className="super-admin-subtitle">{currentUser.full_name} ({currentUser.email})</p>
          </div>
          <button type="button" className="super-admin-logout" onClick={handleLogout} disabled={submitting}>
            Logout
          </button>
        </div>

        <nav className="super-admin-top-nav" aria-label="Super admin navigation">
          <button
            type="button"
            className={`super-admin-nav-btn ${activeView === 'workflow' ? 'active' : ''}`}
            onClick={() => setActiveView('workflow')}
          >
            CMS Workflow
          </button>
          <button
            type="button"
            className={`super-admin-nav-btn ${activeView === 'users' ? 'active' : ''}`}
            onClick={() => setActiveView('users')}
          >
            Users & Access
          </button>
          <button
            type="button"
            className={`super-admin-nav-btn ${activeView === 'updates' ? 'active' : ''}`}
            onClick={() => setActiveView('updates')}
          >
            Update History
          </button>
          <button
            type="button"
            className={`super-admin-nav-btn ${activeView === 'admissions' ? 'active' : ''}`}
            onClick={() => setActiveView('admissions')}
          >
            Admissions Admin
          </button>
        </nav>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-info">{success}</div>}
        {loadingData && <div className="alert alert-info">Loading admin data...</div>}

        <div className="super-admin-metrics">
          <article className="super-admin-metric-card">
            <p>Total Users</p>
            <strong>{users.length}</strong>
          </article>
          <article className="super-admin-metric-card">
            <p>Active Accounts</p>
            <strong>{activeUsersCount}</strong>
          </article>
          <article className="super-admin-metric-card">
            <p>Total Updates</p>
            <strong>{updateHistory.length}</strong>
          </article>
          <article className="super-admin-metric-card">
            <p>Updates in Workflow</p>
            <strong>{inReviewCount}</strong>
          </article>
        </div>

        {activeView === 'workflow' && <CmsWorkflowPanel apiBase={apiBase} />}

        {activeView === 'users' && (
          <>
            <article className="super-admin-card super-admin-card-toolbar">
              <div className="super-admin-toolbar">
                <h3>User Management</h3>
                <button
                  type="button"
                  className="super-admin-toggle-form"
                  onClick={() => setShowCreateUserForm((prev) => !prev)}
                >
                  {showCreateUserForm ? 'Close Form' : 'Add User'}
                </button>
              </div>

              {showCreateUserForm && (
                <form className="super-admin-create-form" onSubmit={handleCreateUser}>
                  <label>
                    Full Name
                    <input
                      type="text"
                      value={newUserForm.full_name}
                      onChange={(event) => setNewUserForm((prev) => ({ ...prev, full_name: event.target.value }))}
                      required
                    />
                  </label>
                  <label>
                    Email
                    <input
                      type="email"
                      value={newUserForm.email}
                      onChange={(event) => setNewUserForm((prev) => ({ ...prev, email: event.target.value }))}
                      required
                    />
                  </label>
                  <label>
                    Password
                    <input
                      type="password"
                      minLength={8}
                      value={newUserForm.password}
                      onChange={(event) => setNewUserForm((prev) => ({ ...prev, password: event.target.value }))}
                      required
                    />
                  </label>

                  <div className="role-checkbox-grid">
                    {adminRoleOptions.map((role) => (
                      <label key={role.role_code} className="role-checkbox-item">
                        <input
                          type="checkbox"
                          checked={newUserForm.role_codes.includes(role.role_code)}
                          onChange={() =>
                            setNewUserForm((prev) => ({
                              ...prev,
                              role_codes: onRoleToggle(prev, role.role_code),
                            }))
                          }
                        />
                        <span>{roleCodeToLabel(role.role_code)}</span>
                      </label>
                    ))}
                  </div>

                  <label className="inline-checkbox">
                    <input
                      type="checkbox"
                      checked={newUserForm.is_active}
                      onChange={(event) => setNewUserForm((prev) => ({ ...prev, is_active: event.target.checked }))}
                    />
                    Active account
                  </label>

                  <button type="submit" disabled={submitting}>
                    {submitting ? 'Saving...' : 'Create User'}
                  </button>
                </form>
              )}
            </article>

            <article className="super-admin-card">
              <h3>Users & Access Rights</h3>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Roles</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => {
                      const edit = userEdits[user.id] || { role_codes: [], is_active: false };
                      return (
                        <tr key={user.id}>
                          <td>
                            <strong>{user.full_name}</strong>
                            <p>{user.email}</p>
                          </td>
                          <td>
                            <div className="role-checkbox-grid compact">
                              {adminRoleOptions.map((role) => (
                                <label key={`${user.id}-${role.role_code}`} className="role-checkbox-item">
                                  <input
                                    type="checkbox"
                                    checked={edit.role_codes.includes(role.role_code)}
                                    onChange={() =>
                                      setUserEdits((prev) => ({
                                        ...prev,
                                        [user.id]: {
                                          ...edit,
                                          role_codes: onRoleToggle(edit, role.role_code),
                                        },
                                      }))
                                    }
                                  />
                                  <span>{role.role_code}</span>
                                </label>
                              ))}
                            </div>
                          </td>
                          <td>
                            <label className="inline-checkbox">
                              <input
                                type="checkbox"
                                checked={Boolean(edit.is_active)}
                                onChange={(event) =>
                                  setUserEdits((prev) => ({
                                    ...prev,
                                    [user.id]: {
                                      ...edit,
                                      is_active: event.target.checked,
                                    },
                                  }))
                                }
                              />
                              {edit.is_active ? 'Active' : 'Inactive'}
                            </label>
                          </td>
                          <td>
                            <button type="button" onClick={() => handleUpdateUserAccess(user.id)} disabled={submitting}>
                              Save Access
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </article>
          </>
        )}

        {activeView === 'updates' && (
          <article className="super-admin-card">
            <h3>Content Update History</h3>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Edited At</th>
                    <th>By</th>
                    <th>Content</th>
                    <th>Action</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {updateHistory.map((entry) => (
                    <tr key={entry.id}>
                      <td>{entry.edited_at}</td>
                      <td>{entry.actor_name}</td>
                      <td>{entry.content_title}</td>
                      <td>{entry.action}</td>
                      <td>{entry.status_code}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </article>
        )}

        {activeView === 'admissions' && <AdmissionsAdminPanel apiBase={apiBase} />}
      </div>
    </section>
  );
}

export default SuperAdminPanel;
